import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { useEffect, useRef, useState } from 'react';
import { deleteSyncCommands, fetchSyncCommands, ICommand } from '../../../../libraries/helpdesk/sync-commands';
import Header from '../../../components/util/header';
import { Table, Loader, Group } from '@mantine/core';
import PaginationComponent from '../../../components/pagination';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../../utils/date';
import { formatSyncCommand } from '../../utils';
import SyncCommandModal from './sync-command-modal';
import { StatusType } from '../../../../libraries/zps';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import { IconX } from '@tabler/icons-react';
import './styles.scss';

const HelpDeskSyncCommands = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const controller = useRef(new AbortController()).current;
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const [list, setList] = useState<ICommand[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();
  const [selectedCommand, setSelectedCommand] = useState<any>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  const { handleApiError, showStatusOperDialog } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadSyncCommands();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadSyncCommands = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchSyncCommands(
      controller,
      offset,
      limit,
      true,
      formatDateToISO(filterBarData?.beginDateTime ?? undefined),
      formatDateToISO(filterBarData?.endDateTime ?? undefined),
      filterBarData?.entity || 0,
      filterBarData?.eid || 0
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.list || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteCommand = async (commandId: number) => {
    try {
      const response = await deleteSyncCommands(commandId);

      if (isZarphSuccessResponse(response)) {
        showStatusOperDialog({
          type: StatusType.INFO,
          message: lang.SYNC_COMMANDS_DELETE_SUCCESS || 'Sync command deleted',
          statusOper: response.data.statusOper
        });

        loadSyncCommands(); // refresh
      } else {
        handleApiError(response, lang.SYNC_COMMANDS_DELETE_ERROR);
      }
    } catch (err) {
      handleApiError(err, lang.SYNC_COMMANDS_DELETE_ERROR);
    }
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const openModal = (command: any) => {
    setSelectedCommand(command);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedCommand(null);
    setShowModal(false);
  };

  return (
    <div className="sync-commands">
      <Header pageTitle={lang.GLOBAL_SYNC_COMMANDS} />

      <HelpdeskFilterBar
        onSearch={loadSyncCommands}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{ beginDateTime: true, endDateTime: true, eid: true, entity: true }}
      />

      <SyncCommandModal show={showModal} handleClose={closeModal} command={selectedCommand} />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.GLOBAL_COMMAND_ID}</Table.Th>
              <Table.Th>Gid</Table.Th>
              <Table.Th>Eid</Table.Th>
              <Table.Th>{lang.GLOBAL_OPERATION}</Table.Th>
              <Table.Th>{lang.GLOBAL_DATE_TIME}</Table.Th>
              <Table.Th>{lang.GLOBAL_COMMAND}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {!list.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={7} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.map((command, index) => (
                    <Table.Tr key={index} onClick={() => openModal(command)} style={{ cursor: 'pointer' }}>
                      <Table.Td>{command.commandId}</Table.Td>
                      <Table.Td>{command.gidKey}</Table.Td>
                      <Table.Td>{command.eidKey}</Table.Td>
                      <Table.Td>{command.operation}</Table.Td>
                      <Table.Td>{formatDateTimeNSecLabel(command.datetime, region)}</Table.Td>
                      <Table.Td>{formatSyncCommand(command.command, lang)}</Table.Td>
                      <Table.Td>
                        <IconX
                          stroke={2}
                          style={{ cursor: 'pointer' }}
                          onClick={event => {
                            event.stopPropagation();
                            deleteCommand(command.commandId);
                          }}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default HelpDeskSyncCommands;
